import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { Control, Controller, FieldValues, Path, RegisterOptions, UseFormReturn } from 'react-hook-form';
import { useGetUseCasesApi, EnumOption, UseCaseOption, UseCasesResponse } from 'src/api/campaignRegistry';

// Enhanced interface that supports both react-hook-form and simple value/onChange
export interface UseCaseSelectorProps<T extends FieldValues = FieldValues> {
  form?: UseFormReturn<T>;
  label?: string;
  defaultUseCase?: string;
  useCaseField?: Path<T>;
  error?: string | boolean;
  validation?: RegisterOptions;
  disabled?: boolean;
  useCasesData?: UseCasesResponse;
  // Alternative to form-based approach
  value?: string;
  onChange?: (value: string) => void;
}

export const UseCaseSelector = <T extends FieldValues = FieldValues>({
  form,
  label,
  defaultUseCase,
  useCaseField = 'useCase' as Path<T>,
  error,
  validation,
  disabled = false,
  useCasesData: externalUseCasesData,
  value,
  onChange,
}: UseCaseSelectorProps<T>) => {
  const { watch, setValue } = form || {};
  const isRequired = !!validation?.required;

  // Support both form-based and value/onChange approaches
  const useCase = form ? watch?.(useCaseField) : value;

  const [useCases, setUseCases] = useState<EnumOption[]>([]);
  const [useCasesData, setUseCasesData] = useState<UseCasesResponse>({});
  const [loadingUseCases, setLoadingUseCases] = useState(false);

  const getUseCases = useGetUseCasesApi();

  // Fetch use cases on component mount or use external data
  useEffect(() => {
    const fetchUseCases = async () => {
      try {
        setLoadingUseCases(true);
        const response = await getUseCases();
        if (response.data && Object.keys(response.data).length > 0) {
          setUseCasesData(response.data);

          const finalOptions = Object.entries(response.data).map(([key, useCase]) => ({
            value: key,
            label: useCase.displayName,
          }));

          setUseCases(finalOptions);
        } else {
          setUseCases([]);
          setUseCasesData({});
        }
      } catch (error) {
        console.error('Failed to fetch use cases:', error);
        setUseCases([]);
        setUseCasesData({});
      } finally {
        setLoadingUseCases(false);
      }
    };

    if (externalUseCasesData && Object.keys(externalUseCasesData).length > 0) {
      // Use external data if provided
      setUseCasesData(externalUseCasesData);
      const finalOptions = Object.entries(externalUseCasesData).map(([key, useCase]) => ({
        value: key,
        label: useCase.displayName,
      }));
      setUseCases(finalOptions);
      setLoadingUseCases(false);
    } else {
      // Fetch data if not provided externally
      fetchUseCases();
    }
  }, [getUseCases, externalUseCasesData]);

  // Set defaults if provided
  useEffect(() => {
    if (defaultUseCase !== undefined && !useCase && setValue) {
      setValue(useCaseField as Path<T>, defaultUseCase as Parameters<typeof setValue>[1]);
    }
  }, [defaultUseCase, useCase, setValue, useCaseField]);


  const getUseCaseDetails = (useCaseKey: string): UseCaseOption | null => {
    if (useCasesData[useCaseKey]) {
      return useCasesData[useCaseKey];
    }

    return null;
  };

  const handleUseCaseChange = useCallback(
    (selectedUseCase: string) => {
      if (form && setValue) {
        setValue(useCaseField as Path<T>, selectedUseCase as Parameters<typeof setValue>[1]);
      } else if (onChange) {
        onChange(selectedUseCase);
      }
    },
    [form, setValue, useCaseField, onChange],
  );

  // Render function for the select component
  const renderSelect = ({ field, fieldState }: { field: { value: string; onChange: (value: string) => void }; fieldState?: { error?: { message?: string } } }) => {
    const currentError = error || fieldState?.error?.message;

    return (
      <FormControl sx={{ marginTop: '10px' }} fullWidth error={!!currentError} required={isRequired}>
        <InputLabel required={isRequired}>{label}</InputLabel>
        <Select
          {...field}
          value={field.value || ""}
          onChange={(e: SelectChangeEvent) => {
            field.onChange(e.target.value);
            handleUseCaseChange(e.target.value);
          }}
          label={label}
          disabled={loadingUseCases || disabled}
          // No height of its own: the outlined label is positioned for the height MUI's own padding gives,
          // so pinning the field to 56px while the inner box is forced to 24px leaves the label sitting above
          // centre on every empty selector.
          sx={{
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
            }
          }}
        >
          {(() => {
            const allOptions = [...useCases];

            // If there's a current useCase value that's not in the loaded options, add it temporarily
            if (field.value && !allOptions.some(option => option.value === field.value)) {
              const useCaseDetails = getUseCaseDetails(field.value);
              allOptions.unshift({
                value: field.value,
                label: useCaseDetails?.displayName || field.value,
              });
            }

            return allOptions.map(option => {
              const useCaseDetails = getUseCaseDetails(option.value);
              return (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography sx={{ flexGrow: 1 }}>{option.label}</Typography>
                    {useCaseDetails?.description && (
                      <Tooltip title={useCaseDetails.description} placement="right" arrow>
                        <Box
                          component="span"
                          sx={{
                            marginLeft: 1,
                            cursor: 'help',
                            typography: 'caption',
                            color: 'text.secondary',
                          }}
                        >
                          ℹ️
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </MenuItem>
              );
            });
          })()}
        </Select>
        {currentError && typeof currentError === 'string' && (
          <Typography
            variant="caption"
            color="error"
            sx={{ marginTop: '8px', marginLeft: '14px' }}
          >
            {currentError}
          </Typography>
        )}
      </FormControl>
    );
  };

  // Use Controller when form is provided, otherwise use direct rendering
  if (form && useCaseField) {
    return (
      <Controller
        name={useCaseField}
        control={form.control as Control<FieldValues>}
        rules={validation}
        render={renderSelect}
      />
    );
  }

  // Fallback for non-form usage
  return renderSelect({ field: { value: useCase || '', onChange: (value: string) => handleUseCaseChange(value) }, fieldState: {} });
};
